import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ContentBody, ContentFooter, ContentHeader, RootContent } from '../styles';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    TextField,
    Typography,
} from '@material-ui/core';
import zxcvbn, { ZXCVBNResult } from 'zxcvbn';
import { Stack } from '@mui/material';
import ErrorIcon from '@material-ui/icons/Error';
import theme from '../../../theme';
import { PasswordResetState } from '../../../pages/auth/action';

export const ResetPassword: React.FC<{
    errorMsg?: string;
    state: PasswordResetState;
    onResetPassword: (newPassword: string) => void;
}> = observer(({ onResetPassword, errorMsg, state }) => {
    const [data, setData] = useState<{ newPassword: string; confNewPassword: string }>({
        newPassword: '',
        confNewPassword: '',
    });
    //Result of security check on newPassword1
    const [zxcvbnResult, setZxcvbnResult] = useState<ZXCVBNResult | null>(null);

    const isValidNewPassword = () => !!data.newPassword && !zxcvbnResult?.feedback?.warning;
    const passwordSatisfied = isValidNewPassword() && data.newPassword === data.confNewPassword;

    const handleChange =
        (prop: keyof { newPassword: string; confNewPassword: string }) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setData({ ...data, [prop]: event.target.value });
        };

    useEffect(() => {
        const zr = zxcvbn(data.newPassword);
        setZxcvbnResult(zr);
    }, [data.newPassword]);

    return (
        <>
            <RootContent>
                <ContentHeader>
                    <Typography variant='h5' gutterBottom>
                        <b>Reset Your Password</b>
                    </Typography>
                </ContentHeader>
                <ContentBody>
                    {(state === PasswordResetState.CODE_VERIFIED ||
                        state === PasswordResetState.SUBMITTING) && (
                        <Card elevation={0}>
                            <CardContent>
                                <Grid container>
                                    {!!errorMsg ? (
                                        <Grid item xs={12}>
                                            <Box display='flex' alignItems='center'>
                                                <ErrorIcon fontSize='large' color='error' />
                                                &nbsp;&nbsp;
                                                <Typography color='textSecondary' variant='body1'>
                                                    {errorMsg}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ) : null}
                                </Grid>

                                <form noValidate autoComplete='off'>
                                    <Grid
                                        container
                                        spacing={1}
                                        alignItems='center'
                                        justifyContent='center'
                                    >
                                        {/* Password */}
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                fullWidth
                                                id='newPassword'
                                                label='New Password'
                                                placeholder='New Password'
                                                type='password'
                                                margin='dense'
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                value={data.newPassword}
                                                disabled={state === PasswordResetState.SUBMITTING}
                                                onChange={handleChange('newPassword')}
                                                required
                                                error={!!zxcvbnResult?.feedback?.warning}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Stack display='flex'>
                                                {zxcvbnResult?.feedback?.warning && (
                                                    <Typography color='error' variant='body1'>
                                                        {zxcvbnResult.feedback.warning}
                                                    </Typography>
                                                )}
                                                {data.newPassword.length > 0 &&
                                                    zxcvbnResult?.feedback?.suggestions && (
                                                        <Typography
                                                            variant='body1'
                                                            style={{
                                                                color: theme.palette.warning.dark,
                                                            }}
                                                        >
                                                            {zxcvbnResult.feedback.suggestions}
                                                        </Typography>
                                                    )}
                                            </Stack>
                                        </Grid>

                                        {/* Password Two */}
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                fullWidth
                                                id='confNewPassword'
                                                label='Confirm Password'
                                                placeholder='New Password again'
                                                type='password'
                                                margin='dense'
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                value={data.confNewPassword}
                                                onChange={handleChange('confNewPassword')}
                                                required
                                                error={
                                                    data.confNewPassword.length > 0 &&
                                                    data.newPassword !== data.confNewPassword
                                                }
                                                disabled={state === PasswordResetState.SUBMITTING}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <Box display='flex' alignItems='center'>
                                                <Typography color='error' variant='body1'>
                                                    {data.confNewPassword.length > 0 &&
                                                        data.newPassword !== data.confNewPassword &&
                                                        'Passwords do not match!'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </form>
                            </CardContent>
                            <div>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        onResetPassword(data.newPassword);
                                    }}
                                    disabled={
                                        !passwordSatisfied ||
                                        state === PasswordResetState.SUBMITTING
                                    }
                                    fullWidth
                                >
                                    Submit
                                </Button>
                            </div>
                        </Card>
                    )}
                </ContentBody>
                <Divider variant='middle' />
                <ContentFooter>
                    <Typography variant='subtitle1' gutterBottom>
                        Changed your mind?{' '}
                        <Button href='/login' color='primary'>
                            Sign in!
                        </Button>
                    </Typography>
                </ContentFooter>
            </RootContent>
        </>
    );
});
